import PerkCard from '@cohort/components-xps/components/cards/PerkCard';
import {DigitalAssetVisual} from '@cohort/components-xps/components/DigitalAssetVisual';
import i18nComponentsXpsInstance from '@cohort/components-xps/lib/i18n';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import type {PerkSpaceViewWDto} from '@cohort/wallet-schemas/perkAccess';
import type {FC} from 'react';
import {getI18n, useTranslation} from 'react-i18next';
import {groupBy, mapValues, pipe} from 'remeda';

type RewardListProps = {
  userOwnerships?: Array<OwnershipWDto>;
  userPerks?: Array<PerkSpaceViewWDto>;
  perks: {
    id: string;
    videoFileKey: string | null;
    thumbnailFileKey: string | null;
    bannerFileKey: string | null;
    displayName: string;
    maxAccessesPerUser: number | null;
  }[];
  collections: {
    id: string;
    smartContract: {
      blockExplorerUrl: string | null;
    } | null;
    imageKey: string | null;
    animationKey: string | null;
    maxOwnershipsPerUser: number | null;
  }[];
  isMobile?: boolean;
};

export const RewardList: FC<RewardListProps> = ({
  userOwnerships,
  userPerks,
  perks,
  collections,
  isMobile,
}) => {
  const {t} = useTranslation('components', {
    keyPrefix: 'rewardList',
    lng: getI18n().language,
    i18n: i18nComponentsXpsInstance,
  });
  const countByCollectionId = userOwnerships
    ? pipe(
        userOwnerships,
        groupBy(ownership => ownership.digitalAsset.collection.id),
        mapValues(ownerships => ownerships.length)
      )
    : null;
  const countByPerkId = userPerks
    ? pipe(
        userPerks,
        groupBy(perk => perk.perk.id),
        mapValues(perks => perks.reduce((acc, perk) => acc + perk.accesses.length, 0))
      )
    : null;

  return (
    <div className="flex flex-col gap-8">
      {collections.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2>{t('digitalAssetsTitle')}</h2>
          <div
            className={cn(
              'gap-5',
              isMobile
                ? 'no-scrollbar -mx-4 flex overflow-auto px-4'
                : 'grid gap-5 [grid-template-columns:repeat(auto-fill,minmax(var(--thin-card-width),1fr))]'
            )}
          >
            {collections.map(collection => (
              <div key={collection.id} className={cn(isMobile && 'w-[var(--thin-card-width)]')}>
                <DigitalAssetVisual
                  title={collection.id}
                  blockExplorerUrl={collection.smartContract?.blockExplorerUrl}
                  digitalAssetImageFileKey={collection.imageKey}
                  animationFileKey={collection.animationKey}
                  canBeObtained={
                    userOwnerships === undefined ||
                    collection.maxOwnershipsPerUser === null ||
                    (countByCollectionId?.[collection.id] ?? 0) < collection.maxOwnershipsPerUser
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {perks.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2>{t('perksTitle')}</h2>
          <div
            className={cn(
              'gap-5',
              isMobile
                ? 'no-scrollbar -mx-4 flex overflow-auto px-4'
                : 'grid grid-cols-1 gap-5 lg:[grid-template-columns:repeat(auto-fill,minmax(var(--perk-card-min-width),1fr))]'
            )}
          >
            {perks.map(perk => (
              <PerkCard
                key={perk.id}
                perkCard={{
                  perkVideoFileKey: perk.videoFileKey,
                  perkThumbnailFileKey: perk.thumbnailFileKey,
                  perkBannerFileKey: perk.bannerFileKey,
                  perkDisplayName: perk.displayName,
                  hideMediaCover:
                    isMobile && perk.bannerFileKey === null && perk.thumbnailFileKey === null,
                  availableInstances: 1,
                  canBeObtained:
                    userPerks === undefined ||
                    perk.maxAccessesPerUser === null ||
                    (countByPerkId?.[perk.id] ?? 0) < perk.maxAccessesPerUser,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default RewardList;
